import { useState, useEffect, useRef } from "react";
import { Menu, X, Facebook, Instagram } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

const navLinks = [
  { name: "Főoldal", href: "#hero" },
  { name: "Szolgáltatások", href: "#usp" },
  { name: "Rólam", href: "#about" },
  { name: "Kapcsolat", href: "#contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  const menuOverlayRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLButtonElement[]>([]);
  const socialIconsRef = useRef<HTMLDivElement>(null);
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prefersReducedMotion = useRef(
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll lock when menu is open
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Lock body scroll
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      
      // Prevent iOS touch scroll
      document.body.style.touchAction = "none";
      
      return () => {
        // Restore scroll
        const scrollY = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        document.body.style.touchAction = "";
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || "0") * -1);
        }
      };
    }
  }, [isOpen, isClosing]);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isClosing) {
        toggleMenu();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, isClosing]);

  // Focus trap - delay to avoid immediate purple outline
  useEffect(() => {
    if (isOpen && firstMenuItemRef.current && !isClosing) {
      // Delay focus to avoid immediate purple outline
      const timer = setTimeout(() => {
        firstMenuItemRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isClosing]);

  // GSAP animations for mobile menu
  useGSAP(
    () => {
      if (!isOpen || isClosing) return;

      const ctx = gsap.context(() => {
        if (prefersReducedMotion.current) {
          // Reduced motion: just show/hide
          if (menuOverlayRef.current) {
            gsap.set(menuOverlayRef.current, { opacity: 1 });
          }
          if (menuContentRef.current) {
            gsap.set(menuContentRef.current, { opacity: 1 });
          }
          menuItemsRef.current.forEach((item) => {
            if (item) gsap.set(item, { opacity: 1, y: 0 });
          });
          if (socialIconsRef.current) {
            gsap.set(socialIconsRef.current, { opacity: 1 });
          }
          return;
        }

        // Overlay fade in
        if (menuOverlayRef.current) {
          gsap.from(menuOverlayRef.current, {
            opacity: 0,
            duration: 0.3,
            ease: "power2.out",
            force3D: true,
          });
        }

        // Menu items stagger in
        menuItemsRef.current.forEach((item, index) => {
          if (item) {
            gsap.from(item, {
              opacity: 0,
              y: 12,
              duration: 0.5,
              delay: index * 0.08,
              ease: "power2.out",
              force3D: true,
            });
          }
        });

        // Social icons fade in after menu items
        if (socialIconsRef.current) {
          gsap.from(socialIconsRef.current, {
            opacity: 0,
            duration: 0.4,
            delay: menuItemsRef.current.length * 0.08 + 0.1,
            ease: "power2.out",
            force3D: true,
          });
        }
      });

      return () => ctx.revert();
    },
    { scope: menuContentRef, dependencies: [isOpen, isClosing] }
  );

  // Close animation
  useGSAP(
    () => {
      if (!isClosing) return;

      const ctx = gsap.context(() => {
        if (prefersReducedMotion.current) {
          // Reduced motion: just hide
          setIsOpen(false);
          setIsClosing(false);
          return;
        }

        // Reverse animation
        const tl = gsap.timeline({
          onComplete: () => {
            setIsOpen(false);
            setIsClosing(false);
          },
        });

        if (menuOverlayRef.current) {
          tl.to(menuOverlayRef.current, {
            opacity: 0,
            duration: 0.25,
            ease: "power2.in",
            force3D: true,
          }, 0);
        }

        menuItemsRef.current.forEach((item, index) => {
          if (item) {
            tl.to(item, {
              opacity: 0,
              y: 12,
              duration: 0.3,
              ease: "power2.in",
              force3D: true,
            }, index * 0.05);
          }
        });

        if (socialIconsRef.current) {
          tl.to(socialIconsRef.current, {
            opacity: 0,
            duration: 0.25,
            ease: "power2.in",
            force3D: true,
          }, 0.1);
        }
      });

      return () => ctx.revert();
    },
    { scope: menuContentRef, dependencies: [isClosing] }
  );

  const handleNavClick = (href: string) => {
    const closeMenu = () => {
      if (isOpen && !isClosing) {
        setIsClosing(true);
      }
    };
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== "/") {
      closeMenu();
      // Wait for animation, then navigate
      setTimeout(() => {
        window.location.href = "/" + href;
      }, prefersReducedMotion.current ? 0 : 400);
      return;
    }
    
    closeMenu();
    
    // Wait for menu close animation, then scroll
    const scrollDelay = prefersReducedMotion.current ? 0 : 400;
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 80; // Account for fixed navbar
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition
        });
      }
    }, scrollDelay);
  };

  const toggleMenu = () => {
    if (isOpen && !isClosing) {
      setIsClosing(true);
    } else if (!isOpen && !isClosing) {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background/80 backdrop-blur-md border-b border-border/50"
            : "bg-transparent"
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="container max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold text-foreground"
            >
              KM<span className="text-accent">.</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.href)}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
                >
                  {link.name}
                </button>
              ))}
            </div>

            {/* Social Icons (Desktop) */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              ref={closeButtonRef}
              onClick={toggleMenu}
              className="md:hidden text-foreground p-2"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {(isOpen || isClosing) && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div
            ref={menuOverlayRef}
            className="absolute inset-0 bg-background/95 backdrop-blur-md"
            onClick={toggleMenu}
            style={{ willChange: "opacity" }}
          />
          <div
            ref={menuContentRef}
            className="relative flex flex-col items-center justify-center h-full gap-8"
          >
            {navLinks.map((link, index) => (
              <button
                key={link.name}
                ref={(el) => {
                  if (el) {
                    menuItemsRef.current[index] = el;
                    if (index === 0) firstMenuItemRef.current = el;
                  }
                }}
                onClick={() => handleNavClick(link.href)}
                className="text-2xl font-heading font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
                style={{ willChange: "transform, opacity" }}
              >
                {link.name}
              </button>
            ))}
            
            {/* Social Icons (Mobile) */}
            <div
              ref={socialIconsRef}
              className="flex items-center gap-6 mt-4"
              style={{ willChange: "opacity" }}
            >
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
